import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/api";

// Simple in-memory cache with timestamps
const cache = {
  exchanges: null,
  coins: {},
  backtests: {},
  timestamps: {},
};

// Cache TTL in milliseconds (15 minutes)
const CACHE_TTL = 15 * 60 * 1000;

// Helper function to check if cache is valid
const isCacheValid = (key) => {
  if (!cache.timestamps[key]) return false;
  return Date.now() - cache.timestamps[key] < CACHE_TTL;
};

// Helper function to set cache with timestamp
const setCache = (key, data) => {
  cache.timestamps[key] = Date.now();
  return data;
};

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Token management
const TOKEN_KEY = "auth_token";
let isConnecting = false;
let lastConnectKey = null;
let lastConnectAt = 0;

export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    // Avoid re-auth loops by marking presence
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch (_) {}
  } else {
    localStorage.removeItem(TOKEN_KEY);
    delete api.defaults.headers.common["Authorization"];
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch (_) {}
  }
};

export const getAuthToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

// Initialize token from localStorage on app start
const existingToken = getAuthToken();
if (existingToken) {
  setAuthToken(existingToken);
}

// Add request interceptor for debugging
api.interceptors.request.use(
  (config) => {
    // Only log in development mode
    if (process.env.NODE_ENV === "development") {
      console.log(
        `Making ${config.method?.toUpperCase()} request to ${config.url}`,
      );
    }

    // Add auth token if available
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      if (process.env.NODE_ENV === "development") {
        console.log(
          `Set Authorization header: Bearer ${token.substring(0, 20)}...`,
        );
      }
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Combined response interceptor for debugging, error handling, and token expiration
api.interceptors.response.use(
  (response) => {
    // Only log in development mode
    if (process.env.NODE_ENV === "development") {
      console.log(`Response from ${response.config.url}:`, response.status);
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // If we had a token and still got 401, consider it invalid/stale and clear it
      const hadToken = !!getAuthToken();
      if (hadToken) {
        if (process.env.NODE_ENV === "development") {
          console.log(
            "Received 401 with token present. Clearing token and dispatching tokenExpired",
          );
        }
        setAuthToken(null);
        try {
          window.dispatchEvent(new CustomEvent("tokenExpired"));
        } catch (_) {}
      }
    }
    return Promise.reject(error);
  },
);

// Wallet API
export const getAuthMessage = async (walletAddress) => {
  try {
    const response = await api.get(`/wallet/auth-message/${walletAddress}`);
    return response.data;
  } catch (error) {
    console.error("Error getting auth message:", error);
    throw error;
  }
};

export const authenticateWallet = async (
  walletAddress,
  message,
  signature,
  networkId,
) => {
  try {
    const response = await api.post("/wallet/authenticate", {
      wallet_address: walletAddress,
      message: message,
      signature: signature,
      network_id: networkId,
    });

    if (process.env.NODE_ENV === "development") {
      console.log(
        "Backend response structure:",
        JSON.stringify(response.data, null, 2),
      );
    }

    // Store the auth token if authentication was successful
    // The backend returns the token at response.data.token
    if (response.data && response.data.token) {
      setAuthToken(response.data.token);
      if (process.env.NODE_ENV === "development") {
        console.log("Auth token stored successfully from response.data.token");
      }
    } else if (response.data && response.data.auth_token) {
      setAuthToken(response.data.auth_token);
      if (process.env.NODE_ENV === "development") {
        console.log(
          "Auth token stored successfully from response.data.auth_token",
        );
      }
    } else if (
      response.data &&
      response.data.wallet &&
      response.data.wallet.auth_token
    ) {
      setAuthToken(response.data.wallet.auth_token);
      if (process.env.NODE_ENV === "development") {
        console.log("Auth token stored successfully from wallet.auth_token");
      }
    } else {
      console.error("No auth token found in response:", response.data);
    }

    return response.data;
  } catch (error) {
    console.error("Error authenticating wallet:", error);
    throw error;
  }
};

export const connectWallet = async (
  walletAddress,
  privateKey = null,
  networkId = null,
) => {
  if (!walletAddress) {
    console.error("Cannot connect wallet: missing wallet address");
    return { error: "Missing wallet address" };
  }

  if (!networkId) {
    console.error("Cannot connect wallet: network ID is required");
    return { error: "Network ID is required" };
  }

  // If a token exists, short-circuit without extra user-wallet fetches to avoid duplicates
  const existingToken = getAuthToken();
  if (existingToken) {
    return {
      success: true,
      alreadyAuthenticated: true,
      token: existingToken,
      wallet: { address: walletAddress, network_id: networkId },
    };
  }

  // Debounce/lock duplicate connection attempts
  const connectKey = `${walletAddress}_${networkId || ""}`;
  if (isConnecting && connectKey === lastConnectKey) {
    return { error: "Wallet connection already in progress" };
  }
  if (connectKey === lastConnectKey && Date.now() - lastConnectAt < 3000) {
    return { error: "Please wait before retrying wallet connection" };
  }

  isConnecting = true;
  lastConnectKey = connectKey;
  try {
    console.log(
      `🔐 Starting wallet connection for: ${walletAddress} on network: ${networkId}`,
    );

    // Check if we have MetaMask for signature
    if (
      typeof window.ethereum !== "undefined" &&
      typeof window.ethereum.request === "function"
    ) {
      // Prefer MetaMask provider if multiple injected providers exist (avoid Coinbase interception)
      const injected = window.ethereum;
      const provider =
        injected && Array.isArray(injected.providers)
          ? injected.providers.find((p) => p && p.isMetaMask) ||
            injected.providers[0]
          : injected;
      // Get auth message
      console.log("📨 Getting auth message...");
      const authResponse = await getAuthMessage(walletAddress);
      const message = authResponse.message;

      // Request signature from MetaMask
      console.log("✍️ Requesting signature from MetaMask...");
      let signature;
      try {
        // Ensure we have account access and the active account matches the requested address
        const accounts = await provider.request({
          method: "eth_requestAccounts",
        });
        const activeAccount =
          Array.isArray(accounts) && accounts.length > 0 ? accounts[0] : null;
        if (!activeAccount) {
          throw new Error("No accounts available in MetaMask");
        }
        if (
          activeAccount.toLowerCase() !== String(walletAddress).toLowerCase()
        ) {
          throw new Error(
            "Active MetaMask account does not match selected address",
          );
        }

        // Some providers require hex-encoded message; attempt plain first with a timeout, then fallback
        const signWithTimeout = (params) => {
          return new Promise((resolve, reject) => {
            let settled = false;
            const timer = setTimeout(() => {
              if (!settled) {
                settled = true;
                reject(new Error("Signature request timed out"));
              }
            }, 20000);
            provider
              .request({ method: "personal_sign", params })
              .then((res) => {
                if (!settled) {
                  settled = true;
                  clearTimeout(timer);
                  resolve(res);
                }
              })
              .catch((err) => {
                if (!settled) {
                  settled = true;
                  clearTimeout(timer);
                  reject(err);
                }
              });
          });
        };

        try {
          // Try standard MetaMask param order first
          signature = await signWithTimeout([message, activeAccount]);
        } catch (primaryErr) {
          // Fallback to hex-encoded message if plain UTF-8 fails
          const encoder = new TextEncoder();
          const hexMsg =
            "0x" +
            Array.from(encoder.encode(message))
              .map((b) => b.toString(16).padStart(2, "0"))
              .join("");
          try {
            signature = await signWithTimeout([hexMsg, activeAccount]);
          } catch (secondaryErr) {
            // Last resort: some providers expect [account, message]
            signature = await signWithTimeout([activeAccount, hexMsg]);
          }
        }
      } catch (signatureError) {
        // Handle specific MetaMask signature rejection
        if (signatureError.code === 4001) {
          // User rejected the signature request
          throw new Error("Transaction signature was rejected by user");
        } else if (signatureError.code === -32603) {
          // Internal JSON-RPC error (often network issues)
          throw new Error("Network error during signature request");
        } else if (
          signatureError.message &&
          signatureError.message.includes("timed out")
        ) {
          throw new Error("Signature request timed out");
        } else if (
          signatureError.message &&
          signatureError.message.includes("does not match")
        ) {
          throw new Error(
            "Active wallet in MetaMask differs from selected address",
          );
        } else {
          // Other MetaMask errors
          throw new Error(
            `MetaMask error: ${signatureError.message || "Unknown error"}`,
          );
        }
      }

      // Authenticate with signature
      console.log("🔑 Authenticating with backend...");
      const authRes = await authenticateWallet(
        walletAddress,
        message,
        signature,
        networkId,
      );

      // Store connection info for persistence
      const token = getAuthToken();

      // Verify that we actually got a valid token from the authentication
      if (!token || token === "undefined" || token === "null") {
        console.error("No valid auth token received after authentication");
        setAuthToken(null); // Clear any invalid token
        return { error: "Authentication failed - no valid token received" };
      }

      const backendWallet = authRes?.wallet || {};
      const normalized = {
        success: true,
        token,
        wallet: {
          address: walletAddress,
          network_id: networkId,
          balance_eth: backendWallet.balance_eth ?? 0,
          balance_bnb: backendWallet.balance_bnb ?? 0,
          balance_usdt: backendWallet.balance_usdt ?? 0,
          balance_gtai: backendWallet.balance_gtai ?? 0,
        },
        raw: authRes,
      };

      localStorage.setItem("connectedAddress", walletAddress);
      localStorage.setItem("connectionMethod", "metamask");
      return normalized;
    } else {
      // Fallback for testing without MetaMask
      console.warn(
        "MetaMask not available, wallet connection requires MetaMask for security",
      );
      return { error: "MetaMask is required for wallet authentication" };
    }
  } catch (error) {
    console.error("Error connecting wallet:", error);

    // Clear any existing auth token on error to prevent false success states
    setAuthToken(null);

    // Provide specific error messages for common cases
    let errorMessage = error.message || "Failed to connect wallet";

    if (errorMessage.includes("rejected by user")) {
      errorMessage = "Wallet connection cancelled by user";
    } else if (errorMessage.includes("Network error")) {
      errorMessage =
        "Network error - please check your connection and try again";
    } else if (errorMessage.includes("MetaMask error")) {
      errorMessage = `MetaMask error: ${errorMessage.split("MetaMask error: ")[1]}`;
    }

    return { error: errorMessage };
  } finally {
    isConnecting = false;
    lastConnectAt = Date.now();
  }
};

export const disconnectWallet = () => {
  setAuthToken(null);
  localStorage.removeItem("connectedAddress");
  localStorage.removeItem("connectionMethod");
};

// Exchanges API
export const getExchanges = async (forceRefresh = false) => {
  const cacheKey = "exchanges";

  // Use cached data if available and not forcing refresh
  if (cache.exchanges && isCacheValid(cacheKey) && !forceRefresh) {
    if (process.env.NODE_ENV === "development") {
      console.log("Using cached exchanges data");
    }
    return cache.exchanges;
  }

  try {
    if (process.env.NODE_ENV === "development") {
      console.log("Fetching exchanges from API");
    }
    const response = await api.get("/exchanges");
    const data = Array.isArray(response.data)
      ? response.data
      : Array.isArray(response.data.exchanges)
        ? response.data.exchanges
        : [];

    // Update cache
    cache.exchanges = setCache(cacheKey, data);
    return data;
  } catch (error) {
    console.error("Error fetching exchanges:", error);
    return [];
  }
};

// Coins API
export const getCoins = async (dexName, topN = null, forceRefresh = false) => {
  // Validate dexName to prevent requests with undefined
  if (!dexName) {
    console.warn("getCoins called with undefined or empty dexName");
    return [];
  }

  // Create cache key that includes topN parameter
  const cacheKey = `${dexName}_${topN || "all"}`;

  // Use cached data if available and not forcing refresh
  if (
    cache.coins[cacheKey] &&
    isCacheValid(`coins_${cacheKey}`) &&
    !forceRefresh
  ) {
    if (process.env.NODE_ENV === "development") {
      console.log(`Using cached ${dexName} coins data (top: ${topN || "all"})`);
    }
    return cache.coins[cacheKey];
  }

  try {
    // Fetch all coins without top parameter to get all filtered tokens
    const url = topN ? `/coins/${dexName}?top=${topN}` : `/coins/${dexName}`;

    if (process.env.NODE_ENV === "development") {
      console.log(`Fetching coins from ${url}`);
    }
    const response = await api.get(url);
    const data = Array.isArray(response.data)
      ? response.data
      : Array.isArray(response.data.coins)
        ? response.data.coins
        : [];

    // Update cache with specific key that includes topN
    cache.coins[cacheKey] = data;
    setCache(`coins_${cacheKey}`, data);

    if (process.env.NODE_ENV === "development") {
      console.log(`Cached ${data.length} coins for ${cacheKey}`);
    }
    return data;
  } catch (error) {
    console.error(`Error fetching coins for exchange ${dexName}:`, error);
    return [];
  }
};

// User Wallet API
export const createUserWallet = async (userAddress, networkId) => {
  try {
    const response = await api.post("/user-wallet/create", {
      user_address: userAddress,
      network_id: networkId,
    });
    return response.data;
  } catch (error) {
    console.error("Error creating user wallet:", error);
    throw error;
  }
};

export const confirmFundTransfer = async (userAddress, txHash, networkId) => {
  try {
    const response = await api.post("/user-wallet/fund", {
      user_address: userAddress,
      tx_hash: txHash,
      network_id: networkId,
    });
    return response.data;
  } catch (error) {
    console.error("Error confirming fund transfer:", error);
    return { error: error.message || "Failed to confirm fund transfer" };
  }
};

export const getUserWallets = async (userAddress, networkId = "1") => {
  try {
    const response = await api.get(
      `/user-wallet/${userAddress}?network_id=${networkId}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error getting user wallets:", error);
    throw error;
  }
};

// Utility function to get trading wallet address from user wallet address
export const getTradingWalletAddress = async (
  userWalletAddress,
  networkId = "1",
) => {
  try {
    const response = await getUserWallets(userWalletAddress, networkId);
    if (response.success && response.wallets && response.wallets.length > 0) {
      // Find the wallet for the current network
      const currentNetworkWallet = response.wallets.find(
        (w) => w.network_id === networkId,
      );
      if (currentNetworkWallet && currentNetworkWallet.trading_address) {
        return currentNetworkWallet.trading_address;
      }
    }
    return null;
  } catch (error) {
    console.error("Error getting trading wallet address:", error);
    return null;
  }
};

export const refreshWalletBalance = async (tradingAddress, networkId) => {
  try {
    const response = await api.get(
      `/user-wallet/refresh-balance?trading_address=${encodeURIComponent(tradingAddress)}&network_id=${encodeURIComponent(networkId)}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error refreshing wallet balance:", error);
    return { error: error.message || "Failed to refresh wallet balance" };
  }
};

export const requestWithdraw = async (
  userAddress,
  networkId,
  withdrawAll,
  tokenSymbol = null,
  amount = null,
) => {
  try {
    const response = await api.post("/user-wallet/withdraw", {
      user_address: userAddress,
      network_id: networkId,
      withdraw_all: withdrawAll,
      token_symbol: tokenSymbol,
      amount: amount,
    });
    return response.data;
  } catch (error) {
    console.error("Error requesting withdraw:", error);
    return { error: error.message || "Failed to request withdraw" };
  }
};

export const getWithdrawStatus = async (withdrawId) => {
  try {
    const response = await api.get(`/user-wallet/withdraw/${withdrawId}`);
    return response.data;
  } catch (error) {
    console.error("Error getting withdraw status:", error);
    return { error: error.message || "Failed to get withdraw status" };
  }
};

// Trade API - Updated for new flow
export const createTrade = async (
  walletAddress,
  dexName,
  coinAddress,
  coinSymbol,
  side,
  amount,
  takeProfitPercent,
  timeframe = "5m",
) => {
  try {
    if (process.env.NODE_ENV === "development") {
      console.log("Creating trade with params:", {
        walletAddress,
        dexName,
        coinAddress,
        coinSymbol,
        side,
        amount,
        takeProfitPercent,
        timeframe,
      });
    }

    // Get current network ID to ensure consistency
    const networkId = await window.ethereum.request({ method: "eth_chainId" });
    const networkIdDecimal = parseInt(networkId, 16).toString();

    // Step 1: Prepare the trade to get funding instructions
    const prepareResponse = await api.post("/trade/prepare", {
      wallet_address: walletAddress,
      dex_name: dexName,
      coin_address: coinAddress,
      coin_symbol: coinSymbol,
      side: side,
      amount: parseFloat(amount),
      take_profit: parseFloat(takeProfitPercent) / 100, // Send percentage as decimal (1.2% = 0.012)
      network_id: networkIdDecimal,
      timeframe: timeframe,
    });

    if (process.env.NODE_ENV === "development") {
      console.log("Trade preparation response:", prepareResponse.data);
    }

    const { fundingRequired, position } = prepareResponse.data;

    // Step 2: Check if funding is required
    // Handle different funding scenarios
    const noFundingRequired = (
      (fundingRequired.usdt_amount === 0 || fundingRequired.usdt_amount <= 0) &&
      (fundingRequired.gas_fee_amount === 0 || fundingRequired.gas_fee_amount <= 0)
    );
    
    if (noFundingRequired) {
      if (process.env.NODE_ENV === "development") {
        console.log(
          "🔍 DEBUG: No funding required, position ready to execute:",
          position,
        );
      }

      // Return the position directly - trade will be executed by backend
      return {
        success: true,
        position: position,
        message:
          fundingRequired.message ||
          "Trade will be executed with available balance",
      };
    }
    
    // Additional check: if position exists and no transactions are required, return it
    if (position && (!fundingRequired.transactions || fundingRequired.transactions.length === 0)) {
      if (process.env.NODE_ENV === "development") {
        console.log(
          "🔍 DEBUG: Position created with no transactions required:",
          position,
        );
      }
      
      return {
        success: true,
        position: position,
        message: "Position created successfully",
      };
    }

    // Step 3: Execute funding transaction based on the funding requirements
    let txHash;

    if (process.env.NODE_ENV === "development") {
      console.log("🔍 DEBUG: Funding transaction details:", {
        side,
        fundingRequired: {
          trading_address: fundingRequired.trading_address,
          usdt_amount: fundingRequired.usdt_amount,
          gas_fee_amount: fundingRequired.gas_fee_amount,
          gas_fee_currency: fundingRequired.gas_fee_currency,
          message: fundingRequired.message,
        },
      });
    }

    // Handle gas-only funding (when USDT is sufficient but gas is needed)
    if (
      fundingRequired.usdt_amount === 0 &&
      fundingRequired.gas_fee_amount > 0
    ) {
      if (process.env.NODE_ENV === "development") {
        console.log("🔍 DEBUG: Gas-only funding required");
      }

      // Send native currency (ETH/BNB) for gas
      const gasAmountWei = Math.floor(
        fundingRequired.gas_fee_amount * Math.pow(10, 18),
      );
      const gasAmountHex = "0x" + gasAmountWei.toString(16);

      if (process.env.NODE_ENV === "development") {
        console.log("🔍 DEBUG: Gas Transaction Details:", {
          gasAmount: fundingRequired.gas_fee_amount,
          gasCurrency: fundingRequired.gas_fee_currency,
          gasAmountWei,
          gasAmountHex,
          tradingAddress: fundingRequired.trading_address,
        });
      }

      txHash = await window.ethereum.request({
        method: "eth_sendTransaction",
        params: [
          {
            from: walletAddress,
            to: fundingRequired.trading_address,
            value: gasAmountHex,
            gas: "0x5208", // Standard gas limit for ETH/BNB transfer
          },
        ],
      });
    }
    // Handle funding based on transaction details provided by backend
    else if (
      fundingRequired.transactions &&
      fundingRequired.transactions.length > 0
    ) {
      if (process.env.NODE_ENV === "development") {
        console.log("🔍 DEBUG: Using backend-provided transaction details:", {
          requiresMultiple: fundingRequired.requires_multiple_transactions,
          transactionCount: fundingRequired.transactions.length,
          sequence: fundingRequired.transaction_sequence,
        });
      }

      const transactionHashes = [];

      // Execute transactions in the specified sequence
      for (const transaction of fundingRequired.transactions) {
        if (process.env.NODE_ENV === "development") {
          console.log("🔍 DEBUG: Executing transaction:", {
            type: transaction.transaction_type,
            to: transaction.to,
            value: transaction.value,
            amount: transaction.amount,
            currency: transaction.currency,
          });
        }

        const txRequest = {
          from: walletAddress,
          to: transaction.to,
          value: transaction.value,
          gas: transaction.gas,
        };

        // Add data field for contract calls (like USDT transfers)
        if (transaction.data) {
          txRequest.data = transaction.data;
        }

        const txHash = await window.ethereum.request({
          method: "eth_sendTransaction",
          params: [txRequest],
        });

        if (process.env.NODE_ENV === "development") {
          console.log(
            `🔍 DEBUG: ${transaction.transaction_type} transaction sent:`,
            txHash,
          );
        }
        transactionHashes.push(txHash);
      }

      // Use the last transaction hash as the primary one for confirmation
      // The backend will detect all related transactions
      txHash = transactionHashes[transactionHashes.length - 1];

      if (process.env.NODE_ENV === "development") {
        console.log(
          "🔍 DEBUG: All transactions completed. Using primary hash:",
          txHash,
        );
      }
    }
    // Fallback for other funding scenarios
    else {
      return {
        error:
          "Invalid funding configuration. Please check the funding requirements.",
      };
    }

    // Step 3: Confirm funding and execute trade
    if (process.env.NODE_ENV === "development") {
      console.log(`Confirming trade with transaction hash: ${txHash}`);
    }

    try {
      const confirmResponse = await api.post("/trade/confirm", {
        position_id: position.id,
        tx_hash: txHash,
      });

      if (process.env.NODE_ENV === "development") {
        console.log("Trade confirmed:", confirmResponse.data);
      }

      // Handle pending confirmation case
      if (confirmResponse.data.pending) {
        return {
          success: true,
          pending: true,
          position: confirmResponse.data.position,
          message: confirmResponse.data.message,
          confirmations: confirmResponse.data.confirmations || 0,
          required_confirmations:
            confirmResponse.data.required_confirmations || 30,
        };
      }

      return confirmResponse.data.position || confirmResponse.data;
    } catch (confirmError) {
      console.error("Trade confirmation error:", confirmError);

      // Handle specific funding confirmation errors
      if (confirmError.response?.data?.detail) {
        const errorMessage = confirmError.response.data.detail;

        if (
          errorMessage.includes("Transaction with hash") &&
          errorMessage.includes("not found")
        ) {
          return {
            error:
              "Transaction not found. Please verify the transaction hash and ensure it's on the correct network. The transaction may still be pending confirmation.",
          };
        }

        if (errorMessage.includes("Transaction is pending confirmation")) {
          return {
            error:
              "Transaction is still pending. Please wait for the transaction to be confirmed on the blockchain and try again.",
          };
        }

        if (errorMessage.includes("not sent to the correct address")) {
          return {
            error:
              "Transaction was sent to the wrong address. Please send funds to the correct trading wallet address.",
          };
        }

        if (errorMessage.includes("Transaction failed on blockchain")) {
          return {
            error:
              "Transaction failed on the blockchain. Please check the transaction details and try again.",
          };
        }

        if (errorMessage.includes("Funding confirmation failed")) {
          return {
            error: errorMessage.replace("Funding confirmation failed: ", ""),
          };
        }

        return { error: errorMessage };
      }

      return {
        error: confirmError.message || "Failed to confirm trade funding",
      };
    }
  } catch (error) {
    console.error("Error creating trade:", error);
    return { error: error.message || "Failed to create trade" };
  }
};

// Positions API with SSE
let positionEventSource = null;

export const getPositions = async (userWalletAddress, networkId = "1") => {
  if (!userWalletAddress) {
    console.warn(
      "getPositions called with undefined or empty userWalletAddress",
    );
    return { positions: [] };
  }

  try {
    // Get the trading wallet address for this user wallet
    const tradingWalletAddress = await getTradingWalletAddress(
      userWalletAddress,
      networkId,
    );

    if (!tradingWalletAddress) {
      console.warn(
        "No trading wallet address found for user wallet:",
        userWalletAddress,
      );
      return { positions: [] };
    }

    // Use the correct endpoint that exists in the backend
    const response = await api.get(`/positions/${tradingWalletAddress}`);

    // The backend returns { positions: [...] }
    if (response.data && Array.isArray(response.data.positions)) {
      return { positions: response.data.positions };
    } else if (Array.isArray(response.data)) {
      return { positions: response.data };
    } else {
      console.warn(
        "Unexpected response format from positions API:",
        response.data,
      );
      return { positions: [] };
    }
  } catch (error) {
    console.error("Error fetching positions:", error);
    return { error: error.message || "Failed to fetch positions" };
  }
};

// Cleanup function to close SSE connection
export const cleanupPositionsSSE = () => {
  if (positionEventSource) {
    positionEventSource.close();
    positionEventSource = null;
  }
};

export const getPosition = async (positionId) => {
  if (!positionId) {
    console.warn("getPosition called with undefined or empty positionId");
    return null;
  }

  try {
    const response = await api.get(`/position/${positionId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching position ${positionId}:`, error);
    return null;
  }
};

// Backtest API
export const runBacktest = async (
  dexName,
  coinAddress,
  side,
  amount,
  takeProfit,
  timeframe = "5m",
) => {
  // Validate required parameters
  if (!dexName || !coinAddress) {
    const missingParams = [];
    if (!dexName) missingParams.push("exchange name");
    if (!coinAddress) missingParams.push("coin address");
    return {
      error: `Missing required parameters: ${missingParams.join(", ")}`,
    };
  }

  if (isNaN(parseFloat(amount)) || isNaN(parseFloat(takeProfit))) {
    return { error: "Amount and take profit must be valid numbers" };
  }

  try {
    const response = await api.post("/backtest", {
      dex_name: dexName,
      token_address: coinAddress,
      side: side,
      amount: parseFloat(amount),
      take_profit: parseFloat(takeProfit),
      timeframe: timeframe,
    });
    return response.data;
  } catch (error) {
    return { error: error.message || "Failed to run backtest" };
  }
};

export const subscribeToPositionUpdates = async (
  userWalletAddress,
  networkId,
  onUpdate,
  onError,
) => {
  if (!userWalletAddress) {
    console.warn("Cannot subscribe to position updates without wallet address");
    return null;
  }

  // Get auth token for Authorization header
  const authToken = getAuthToken();
  if (!authToken) {
    console.error("No auth token available for SSE connection");
    onError && onError(new Error("Authentication required for SSE connection"));
    return null;
  }

  // Get the trading wallet address for this user wallet
  const tradingWalletAddress = await getTradingWalletAddress(
    userWalletAddress,
    networkId,
  );

  if (!tradingWalletAddress) {
    console.warn(
      "No trading wallet address found for user wallet:",
      userWalletAddress,
    );
    onError &&
      onError(new Error("No trading wallet found for this user wallet"));
    return null;
  }

  // Create custom SSE client with Authorization header
  let abortController = new AbortController();

  const connectSSE = async () => {
    try {
      const url = `${API_URL}/positions/${tradingWalletAddress}/stream`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "text/event-stream",
          "Cache-Control": "no-cache",
          Authorization: `Bearer ${authToken}`,
        },
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(
          `SSE connection failed: ${response.status} ${response.statusText}`,
        );
      }

      if (process.env.NODE_ENV === "development") {
        console.log(
          "SSE connection opened for trading wallet:",
          tradingWalletAddress,
        );
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          if (process.env.NODE_ENV === "development") {
            console.log("SSE stream ended");
          }
          break;
        }

        const chunk = decoder.decode(value);
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));
              if (process.env.NODE_ENV === "development") {
                console.log("SSE message received:", data);
              }

              // Handle different message types with optimized format support
              if (data.t === "c" || data.type === "connected") {
                if (process.env.NODE_ENV === "development") {
                  console.log(
                    "SSE connection established:",
                    data.m || data.message,
                  );
                }
              } else if (data.t === "pu" || data.type === "positions_update") {
                if (process.env.NODE_ENV === "development") {
                  console.log(
                    `SSE position update: ${(data.p || data.positions).length} positions`,
                  );
                }

                // Convert optimized format to legacy format for backward compatibility
                const positions =
                  data.t === "pu"
                    ? convertOptimizedToLegacyPositions(data.p, data.wa)
                    : data.positions;

                onUpdate(positions);
              } else if (data.t === "e" || data.type === "error") {
                console.error("SSE error message:", data.e || data.error);
                onError && onError(new Error(data.e || data.error));
              } else if (data.t === "d" || data.type === "disconnected") {
                if (process.env.NODE_ENV === "development") {
                  console.log("SSE disconnected:", data.m || data.message);
                }
              } else if (data.t === "p" || data.type === "ping") {
                // Keep-alive message, ignore it
              } else {
                // Fallback for old format (array of positions)
                if (Array.isArray(data)) {
                  if (process.env.NODE_ENV === "development") {
                    console.log("SSE legacy format received");
                  }
                  onUpdate(data);
                } else {
                  console.warn("Unknown SSE message type:", data);
                }
              }
            } catch (error) {
              console.error("Error parsing SSE data:", error);
              onError && onError(error);
            }
          }
        }
      }
    } catch (error) {
      if (error.name === "AbortError") {
        if (process.env.NODE_ENV === "development") {
          console.log("SSE connection aborted");
        }
      } else {
        console.error("SSE connection error:", error);
        onError && onError(error);
      }
    }
  };

  // Start the connection
  connectSSE();

  // Return cleanup function
  return {
    close: () => {
      abortController.abort();
    },
  };
};

// Single price streaming service for all tokens
export const subscribeToAllPriceUpdates = (
  exchange,
  onUpdate,
  onError,
  onVolumeUpdate = null,
) => {
  if (!exchange) return null;

  let abortController = new AbortController();

  const connectPriceSSE = async () => {
    try {
      const response = await fetch(`${API_URL}/prices/${exchange}/stream`, {
        method: "GET",
        headers: {
          Accept: "text/event-stream",
          "Cache-Control": "no-cache",
        },
        signal: abortController.signal,
      });

      if (!response.ok) throw new Error(`SSE failed: ${response.status}`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const lines = decoder.decode(value).split("\n");
        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === "price_update") {
                onUpdate(data.token, data.price);
              } else if (data.type === "volume_update" && onVolumeUpdate) {
                onVolumeUpdate(data.token, data.volume);
              }
            } catch (error) {
              console.error("SSE parse error:", error);
            }
          }
        }
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("SSE error:", error);
        onError?.(error);
      }
    }
  };

  connectPriceSSE();
  return { close: () => abortController.abort() };
};

// Helper function to convert optimized position format to legacy format
const convertOptimizedToLegacyPositions = (
  optimizedPositions,
  walletAddress,
) => {
  if (!Array.isArray(optimizedPositions)) {
    return [];
  }

  return optimizedPositions.map((pos) => ({
    id: pos.i,
    wallet_address: walletAddress,
    dex_name: pos.d,
    coin_address: pos.ca,
    coin_symbol: pos.cs,
    amount: pos.a,
    entry_price: pos.ep,
    current_price: pos.cp,
    take_profit: pos.tp,
    stop_loss: pos.sl,
    side: pos.s,
    status: pos.st,
    pnl: pos.pnl,
    pnl_percentage: pos.pnlp,
    created_at: pos.cat ? new Date(pos.cat).toISOString() : null,
    closed_at: pos.clt ? new Date(pos.clt).toISOString() : null,
    exit_price: pos.exp,
    profit_loss: pos.pl,
    profit_loss_usdt: pos.plu,
    tx_hash: pos.tx,
    network_id: pos.nid,
    base_currency: pos.bc,
    is_short: pos.sh,
    // Convert optimized transactions to legacy format
    transactions: pos.txs
      ? pos.txs.map((tx) => ({
          tx_hash: tx.h,
          tx_type: tx.t,
          status: tx.s,
          network_id: tx.n,
          amount: tx.a,
          price: tx.p,
          fee: tx.f,
          created_at: tx.c ? new Date(tx.c).toISOString() : null,
        }))
      : [],
  }));
};

// Check transaction confirmation status
export const checkTransactionConfirmations = async (
  txHash,
  networkId = "1",
) => {
  try {
    const response = await api.get(
      `/transaction/confirmations/${txHash}?network_id=${networkId}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error checking transaction confirmations:", error);
    return {
      error: error.message || "Failed to check transaction confirmations",
    };
  }
};

// Hyperliquid API
export const prepareHyperliquidTrade = async (
  walletAddress,
  symbol,
  amount,
  isBuy,
) => {
  try {
    // Use the regular trade/prepare endpoint for Hyperliquid
    const response = await api.post("/trade/prepare", {
      wallet_address: walletAddress,
      dex_name: "hyperliquid",
      coin_address: symbol, // For Hyperliquid, symbol is used as coin_address
      coin_symbol: symbol, // Add coin_symbol field for Hyperliquid
      side: isBuy ? "long" : "short",
      amount: parseFloat(amount),
      take_profit: 0.012, // Default 1.2% take profit
      network_id: "offchain", // Hyperliquid is offchain
      timeframe: "5m",
    });
    return response.data;
  } catch (error) {
    console.error("Error preparing Hyperliquid trade:", error);
    return { error: error.message || "Failed to prepare Hyperliquid trade" };
  }
};

export const executeHyperliquidTrade = async (
  walletAddress,
  privateKey,
  symbol,
  amount,
  isBuy,
) => {
  try {
    // For now, return a mock response since Hyperliquid execution is not fully implemented
    return {
      success: true,
      message: "Hyperliquid trade execution is not yet fully implemented",
      symbol: symbol,
      amount: amount,
      side: isBuy ? "long" : "short",
    };
  } catch (error) {
    console.error("Error executing Hyperliquid trade:", error);
    return { error: error.message || "Failed to execute Hyperliquid trade" };
  }
};

export const getHyperliquidPositions = async (
  userWalletAddress,
  networkId = "1",
) => {
  try {
    // Get the trading wallet address for this user wallet
    const tradingWalletAddress = await getTradingWalletAddress(
      userWalletAddress,
      networkId,
    );

    if (!tradingWalletAddress) {
      console.warn(
        "No trading wallet address found for user wallet:",
        userWalletAddress,
      );
      return { positions: [] };
    }

    // Use the regular positions endpoint
    const response = await api.get(`/positions/${tradingWalletAddress}`);
    if (response.data && response.data.positions) {
      // Filter for Hyperliquid positions only
      const hyperliquidPositions = response.data.positions.filter(
        (pos) => pos.dex_name === "hyperliquid",
      );
      return { positions: hyperliquidPositions };
    }
    return { positions: [] };
  } catch (error) {
    console.error("Error getting Hyperliquid positions:", error);
    return { error: error.message || "Failed to get Hyperliquid positions" };
  }
};

export const closeHyperliquidPosition = async (
  walletAddress,
  privateKey,
  symbol,
  size,
) => {
  try {
    // For now, return a mock response since Hyperliquid closing is not fully implemented
    return {
      success: true,
      message: "Hyperliquid position closing is not yet fully implemented",
      symbol: symbol,
      size: size,
    };
  } catch (error) {
    console.error("Error closing Hyperliquid position:", error);
    return { error: error.message || "Failed to close Hyperliquid position" };
  }
};

// Position management API
export const cancelPosition = async (positionId) => {
  try {
    const response = await api.post(`/position/${positionId}/cancel`);
    return response.data;
  } catch (error) {
    console.error("Error cancelling position:", error);
    return { error: error.message || "Failed to cancel position" };
  }
};

export const retryPosition = async (positionId) => {
  try {
    const response = await api.post(`/position/${positionId}/retry`);
    return response.data;
  } catch (error) {
    console.error("Error retrying position:", error);
    return { error: error.message || "Failed to retry position" };
  }
};

export const deletePosition = async (positionId) => {
  try {
    const response = await api.delete(`/position/${positionId}/delete`);
    return response.data;
  } catch (error) {
    console.error("Error deleting position:", error);
    return { error: error.message || "Failed to delete position" };
  }
};

export const closePosition = async (positionId) => {
  try {
    const response = await api.post(`/position/${positionId}/close`);
    return response.data;
  } catch (error) {
    console.error("Error closing position:", error);
    return { error: error.message || "Failed to close position" };
  }
};

export const getPositionDetails = async (positionId) => {
  try {
    const response = await api.get(`/position/${positionId}/details`);
    return response.data;
  } catch (error) {
    console.error("Error fetching position details:", error);
    return { error: error.message || "Failed to fetch position details" };
  }
};

export default api;
